import {MicroserviceResponse} from '@digital-advice/ms-core-sdk';
import {
  ClientService,
  // requests
  ClientServiceOptions,
  ClientLoginsOptions,
  ClUserListOptions,
  GetClientWithSubresourcesOptions,
  GetHouseholdOptions,
  GetUserOptions,
  GetAccountByGoalIdOptions,
  UpdateAccountOptions,
  DeleteAccountRequestOptions,
  PutUserOptions,
  DeActiveClientOptions,
  PostUserOptions,
  GoalRequestOptions,
  DocumentOptions,
  GetAccountSummaryOptions,
  DocumentSummaryOptions,
  DeleteDocumentOptions,
  UploadDocumentByBase64Options,
  GetGoalListOptions,
  UnFinishedAccountOptions,
  AddClientGoalsOptions,
  SearchClientOptions,
  AccountTradeStatusOptions,
  SupervisionClientOptions,
  PostOnboardingDataOptions,
  // responses
  ClientLoginsResponse,
  ClUserListResponse,
  ClientWithSubresourcesResponse,
  HouseholdWithSubresourcesResponse,
  ClientAccount,
  AccountDetails,
  ClientUserSearchResult,
  GetClUserOptions,
  AttachGoalToAccountsOptions,
  AttachGoalToAccountsResponse,
  GoalResponse,
  DocumentSummary,
  AccountSummaryDetailsResponse,
  DocumentUploadResponse,
  UnFinishedAccountResponse,
  ClientGoalResponse,
  SearchClientResponse,
  ClientProfileOptions,
  ClientProfileResponse,
  AccountTradeStatusResponse,
  ClUser,
  SupervisionClientResponse,
  OnboardingResponse,
  GetOnboardingDataOptions,
} from '@digital-advice/ms-client-sdk';
import {apiKey, getLogger, resolveEndpointForServiceToken, interceptors, sourceInfo} from '@advisor/services/sdk';

const svcOptions = {hasServiceTokenEndpoint: false};

export const defaultOptions: ClientServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint: resolveEndpointForServiceToken(svcOptions.hasServiceTokenEndpoint),
  interceptors,
  sourceInfo,
};

export default class ClientServices {
  static async sdkService(options: ClientServiceOptions = defaultOptions): Promise<ClientService> {
    const sdkService = await new ClientService(options);

    return sdkService;
  }

  /**
   * Gateway endpoint: getClientLogins
   * @param {Object} {riaId}
   */
  static async getClientLogins(payload: ClientLoginsOptions): Promise<ClientLoginsResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getClientLogins(payload);
  }

  /**
   * Gateway endpoint: getClUsersList
   * @param {Object} {riaUserId, limit, offset, fullyPopulated, clientFilter, sortField, sortOrder}
   */
  static async getClUsersList(payload: ClUserListOptions): Promise<MicroserviceResponse<ClUserListResponse[]>> {
    const service = await this.sdkService(defaultOptions);

    return service.getClUsersList(payload);
  }

  /**
   * Gateway endpoint: getClientWithSubResources
   * @param {Object} {id, subResources, timeZone, piiProps}
   */
  static async getClientWithSubResources(
    payload: GetClientWithSubresourcesOptions
  ): Promise<ClientWithSubresourcesResponse> {
    const service = await this.sdkService(defaultOptions);
    const {id, subResources, piiProps = []} = payload;
    const newPayload = {id, subResources, piiProps};

    return service.getClientWithSubResources(newPayload);
  }

  /**
   * Gateway endpoint: GetHouseholdOptions
   * @param {Object} {id, subResources, timeZone}
   */
  static async getClientHouseholdGoals(payload: GetHouseholdOptions): Promise<HouseholdWithSubresourcesResponse> {
    const service = await this.sdkService(defaultOptions);
    const {id, subResources} = payload;
    const newPayload = {id, subResources};

    return service.getClientHouseholdGoals(newPayload);
  }

  /**
   * Gateway endpoint: GetUserOptions
   * @param {Object} {clUserId}
   */
  static async getAllAccountsForClientId(payload: GetUserOptions): Promise<unknown> {
    const service = await this.sdkService(defaultOptions);

    return service.getAllAccountsForClientId(payload);
  }

  /**
   * Gateway endpoint: GetAccountByGoalIdOptions
   * @param {Object} {goalId, riaComponent?}
   */
  static async getClientAccountsByGoalId(payload: GetAccountByGoalIdOptions): Promise<ClientAccount[]> {
    const service = await this.sdkService(defaultOptions);

    return service.getClientAccountsByGoalId(payload);
  }

  /**
   * Gateway endpoint: updateAccountDetails
   * @param {Object} {accountId, roboStatus, goalId, minCashValue, distributionAmount, distributionId, portOverrideId, riskTolOverrideValue, requestId}
   */
  static async updateAccountDetails(payload: UpdateAccountOptions): Promise<AccountDetails> {
    const service = await this.sdkService(defaultOptions);

    return service.updateAccountDetails(payload);
  }

  /**
   * Gateway endpoint: deleteClientAccountRequest
   * @param {Object} {clUserId, requestInitiatedFrom, riaComponent, requestId}
   */
  static async deleteClientAccountRequest(payload: DeleteAccountRequestOptions): Promise<void> {
    const service = await this.sdkService(defaultOptions);

    return service.deleteClientAccountRequest(payload);
  }

  /**
   * Gateway endpoint: createUpdateClient
   * Method: POST
   * @param {Object} PostUserOptions
   */
  static async createUpdateClient(payload: PostUserOptions): Promise<ClientUserSearchResult> {
    const service = await this.sdkService(defaultOptions);

    return service.createUpdateClient(payload);
  }

  /**
   * Gateway endpoint: updateClient
   * Method: PUT
   * @param {Object} PutUserOptions
   */
  static async updateClient(payload: PutUserOptions): Promise<ClUser> {
    const service = await this.sdkService(defaultOptions);

    return service.updateClient(payload);
  }

  /**
   * Gateway endpoint: updateClientRoboStatus
   * @param {Object} {clUser, riaComponent, requestId}
   */
  static async updateClientRoboStatus(payload: PutUserOptions): Promise<ClientWithSubresourcesResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.updateClientRoboStatus(payload);
  }

  /**
   * Gateway endpoint: deActiveClient
   * @param {Object} {clUser, riaComponent, requestId}
   */

  static async deActiveClient(payload: DeActiveClientOptions): Promise<ClientWithSubresourcesResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.deActiveClient(payload);
  }

  /**
   * Gateway endpoint: getClUserDetails
   */
  static async getClUserDetails(payload: GetClUserOptions): Promise<unknown> {
    const service = await this.sdkService(defaultOptions);

    return service.getClUserDetails(payload);
  }

  /**
   * Gateway endpoint: AttachGoalToAccounts
   */
  static async attachGoalToAccounts(payload: AttachGoalToAccountsOptions): Promise<AttachGoalToAccountsResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.attachGoalToAccounts(payload);
  }

  /**
   * Gateway endpoint: getGoalByGoalId
   */
  static async getGoalByGoalId(payload: GoalRequestOptions): Promise<GoalResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getGoalByGoalId(payload);
  }

  /**
   * Gateway endpoint: getClUserSSN
   * @param {Object} A {@link GetUserOptions} object.
   */
  static async getClUserSSN(payload: GetUserOptions): Promise<unknown> {
    const service = await this.sdkService(defaultOptions);

    return service.getClUserSSN(payload);
  }

  /**
   * Gateway endpoint: previewDocumentByDocId
   * @param {Object} A {@link DocumentOptions} object.
   */
  static async previewDocumentByDocId(payload: DocumentOptions): Promise<DocumentSummary> {
    const service = await this.sdkService(defaultOptions);

    return service.previewDocumentByDocId(payload);
  }

  /**
   * Gateway endpoint: downloadDocumentByDocId
   * @param {Object} A {@link DocumentOptions} object.
   */
  static async downloadDocumentByDocId(payload: DocumentOptions): Promise<DocumentSummary> {
    const service = await this.sdkService(defaultOptions);

    return service.downloadDocumentByDocId(payload);
  }

  /**
   * Gateway endpoint: account/summary/${accountId}
   * @param {Object} Array of {@link AccountSummaryDetailsResponse} object(s).
   */
  static async getAccountDetails(payload: GetAccountSummaryOptions): Promise<AccountSummaryDetailsResponse[]> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountDetails(payload);
  }

  /**
   * Gateway endpoint: getDocumentSummary
   * @param {Object} A {@link DocumentOptions} object.
   */
  static async getDocumentSummary(payload: DocumentSummaryOptions): Promise<DocumentSummary> {
    const service = await this.sdkService(defaultOptions);

    return service.getDocumentSummary(payload);
  }

  /**
   * Gateway endpoint: deleteDocument
   * @param {Object} A {@link DocumentOptions} object.
   */
  static async deleteDocument(payload: DeleteDocumentOptions): Promise<unknown> {
    const service = await this.sdkService(defaultOptions);

    return service.deleteDocument(payload);
  }

  /**
   * Gateway endpoint: uploadDocumentByBase64
   * @param {Object} A {@link UploadDocumentByBase64Options} object.
   */
  static async uploadDocumentByBase64(payload: UploadDocumentByBase64Options): Promise<DocumentUploadResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.uploadDocumentByBase64(payload);
  }

  /**
   * Gateway endpoint: uploadDocumentByBase64
   * @param {Object} A {@link GetGoalListOptions} object.
   */
  static async getGoalList(payload: GetGoalListOptions): Promise<unknown> {
    const service = await this.sdkService(defaultOptions);

    return service.getGoalList(payload);
  }

  /**
   * Gateway endpoint: uploadDocumentByBase64
   * @param {Object} A {@link UnFinishedAccountOptions} object.
   */
  static async getUnFinishedClientAccounts(payload: UnFinishedAccountOptions): Promise<UnFinishedAccountResponse[]> {
    const service = await this.sdkService(defaultOptions);

    return service.getUnFinishedClientAccounts(payload);
  }

  /**
   * Add client goals
   *
   * @async
   * @param options - A {@link AddClientGoalsOptions} object.
   * @returns A promise of a {@link ClientGoalResponse} object.
   */
  static async addClientGoals(options: AddClientGoalsOptions): Promise<ClientGoalResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.addClientGoals(options);
  }

  /**
   * Updates a goal
   *
   * @async
   * @param options - A {@link GoalRequestOptions} object.
   * @returns A promise of a {@link GoalResponse} object.
   */
  // eslint-disable-next-line max-lines-per-function
  static async updateGoal(options: GoalRequestOptions): Promise<GoalResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.updateGoal(options);
  }

  /**
   * Search Client
   *
   * @async
   * @param options - A {@link SearchClientOptions} object.
   * @returns A promise of a {@link SearchClientResponse} object.
   */
  static async searchClient(options: SearchClientOptions): Promise<SearchClientResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.searchClient(options);
  }

  /**
   * get client profile
   *
   * @async
   * @param options - A {@link ClientProfileOptions} object.
   * @returns A promise of a {@link ClientProfileResponse} object.
   */
  static async getClientProfile(options: ClientProfileOptions): Promise<ClientProfileResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getClientProfile(options);
  }

  /**
   * Check if given account is tradeable or not
   * @param options - A {@link AccountTradeStatusOptions} object
   * @returns A promise of a {@link AccountTradeStatusResponse} object.
   */
  static async getAccountTradeStatus(options: AccountTradeStatusOptions): Promise<AccountTradeStatusResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getAccountTradeStatus(options);
  }

  /**
   * Get Enterprise Clients List
   *
   * @async
   * @param options - A {@link SupervisionClientOptions} object.
   * @returns A promise of a {@link SupervisionClientResponse} object.
   */
  static async getEnterpriseClientList(options: SupervisionClientOptions): Promise<SupervisionClientResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getClientsForSupervision(options);
  }

  /**
   * Save Onboarding Data
   *
   * @async
   * @param options - A {@link PostOnboardingDataOptions} object.
   * @returns A promise of a {@link OnboardingResponse} object.
   */
  static async saveOnboarding(options: PostOnboardingDataOptions): Promise<OnboardingResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.postOnboardingData(options);
  }

  static async getOnboarding(options: GetOnboardingDataOptions): Promise<OnboardingResponse> {
    const service = await this.sdkService(defaultOptions);

    return service.getOnboardingData(options);
  }
}
